import { useQuery } from '@apollo/client';
import { Box, IconButton, InputAdornment, styled } from '@mui/material';
import { Search } from '@styled-icons/bootstrap';
import { Close } from '@styled-icons/ionicons-sharp';
import useGeoDataMutation from 'components/ComponentsLibrary/hooks/useGeoDataMutations';
import { useDueDiligenceProcess } from 'components/DueDiligenceProcess/Context/DueDiligenceProcessContext';
import { useGeoDataCollection } from 'components/DueDiligenceProcess/Context/SubSections/GeoDataCollectionContext';
import DueDiligenceProcessSubSectionNavigation from 'components/DueDiligenceProcess/DueDiligenceProcessSubSectionNavigation';
import { Loader } from 'components/Forms';
import { LoadingOpacity } from 'components/Forms/Loader';
import { SearchInput } from 'components/Forms/SearchToolbar';
import { useMessages } from 'components/hooks';
import {
  Illustration,
  IllustrationWrapper,
} from 'components/Sites/SiteSelectionDialog/SiteSelection';
import { SitesTableColumnKeys } from 'components/Sites/SitesTable/sitesHeaderColumns';
import SitesTable from 'components/Sites/SitesTable/SitesTable';
import { ErrorState, FlexBox, InfiniteScrollWrapper } from 'components/Structure';
import { SITES_TABLE_ROWS_PER_PAGE } from 'constants/pagination';
import { ThemeTypography } from 'designSystem';
import GeoDataItem from 'designSystem/DataDisplay/GeoDataItem/GeoDataItem';
import { GET_SITES } from 'graphql/secure/queries/site.queries';
import useDueDiligenceProcessMutations from 'hooks/useDueDiligenceProcessMutations';
import React, { FC, useCallback, useEffect, useMemo, useState } from 'react';
import { booleanish } from 'types/booleanish.types';
import { EGeoDataEntity, IGeoData } from 'types/geodata.types';
import { ESiteType, IFarmSite, ISite, SiteViewOption } from 'types/site.types';
import { GraphQlConnection } from 'types/types';
import { removeGraphConnections } from 'utils/graphql.utils';
import TraceableLocation from 'assets/img/cards/traceable-illustration.png';

const GeoDataContainer = styled('div')<{ disabled?: booleanish }>(({ disabled }) => ({
  cursor: disabled === 'true' ? 'initial' : 'pointer',
}));

const CloseButton = styled(IconButton)(({ theme }) => ({
  color: theme.palette.text.primary,
  padding: 0,
  margin: 0,
}));

const InfoContainer = styled(FlexBox)(({ theme }) => ({
  padding: theme.spacing(2),
  borderRadius: '6px',
  border: `1px solid ${theme.custom.themeColors.primary[40]}`,
  background: theme.custom.themeColors.primary[5],
}));

const GeoDataCollectionConfirmSelection: FC = () => {
  const [processingLoading, setProcessingLoading] = useState<boolean>(false);
  // We need to use a state for the loading state of the geo data collection instead of the query loading state to differentiate between loading the data and loading the next page of data
  const [geoDataLoading, setGeoDataLoading] = useState<boolean>(false);
  const [searchTerm, setSearchTerm] = useState<string>('');

  const { setErrorMessage } = useMessages();
  const { geoDataProcess } = useDueDiligenceProcess();
  const {
    selectedGeoDataId,
    geoDataSelection,
    onNextClick,
    onPreviousClick,
    setGeoDataSelection,
    setSelectedGeoDataId,
  } = useGeoDataCollection();
  const { updateDueDiligenceProcess } = useDueDiligenceProcessMutations();
  const { startGeoDatasProcessing } = useGeoDataMutation();

  if (!geoDataProcess) throw new Error('Geo data process not found');

  const selectedGeoData = useMemo(
    () => geoDataProcess?.geoDatas.find(geoData => geoData.id === selectedGeoDataId),
    [geoDataProcess, selectedGeoDataId]
  );

  const { data, error, fetchMore } = useQuery<{ sites: GraphQlConnection<ISite> }>(GET_SITES, {
    variables: {
      filters: {
        ...(selectedGeoData?.siteClusterId && { siteClusterId: selectedGeoData.siteClusterId }),
        ...(selectedGeoData?.datasetId && { datasetId: selectedGeoData.datasetId }),
        ...(searchTerm && { searchTerm }),
        ownership: null,
      },
      first: SITES_TABLE_ROWS_PER_PAGE,
      siteViewOption: SiteViewOption.LIST,
    },
    notifyOnNetworkStatusChange: true,
    skip: !selectedGeoData?.id || selectedGeoData?.dataType === EGeoDataEntity.SITE_SELECTION,
    onCompleted: () => setGeoDataLoading(false),
    onError: () => setGeoDataLoading(false),
  });

  const farms: IFarmSite[] = useMemo(
    () =>
      data?.sites
        ? (removeGraphConnections(data.sites).filter(
            site => site.siteType === ESiteType.FARM
          ) as IFarmSite[])
        : [],
    [data]
  );

  const getExcludedSiteIdsForGeoData = useCallback(
    (geoData: IGeoData) => {
      if (geoData.dataType === EGeoDataEntity.DATASET) {
        return (
          geoDataSelection.datasetIdsSelection?.find(dataset => dataset.id === geoData.datasetId)
            ?.excludedSitesIds || []
        );
      }
      if (geoData.dataType === EGeoDataEntity.CLUSTER) {
        return (
          geoDataSelection.clusterIdsSelection?.find(
            cluster => cluster.id === geoData.siteClusterId
          )?.excludedSitesIds || []
        );
      }
      return [];
    },
    [geoDataSelection]
  );

  const excludedSiteIdsFromSelectedGeoData = useMemo(() => {
    if (!selectedGeoData) {
      return [];
    }
    return getExcludedSiteIdsForGeoData(selectedGeoData);
  }, [selectedGeoData, getExcludedSiteIdsForGeoData]);

  const hasNextPage: boolean = !!data?.sites?.pageInfo?.hasNextPage;
  const endCursor: string | undefined = data?.sites?.pageInfo?.endCursor;
  const farmCount: number = data?.sites?.count || 0;

  const handlePageEndReached = async () => {
    if (endCursor) {
      await fetchMore({
        variables: {
          after: endCursor,
        },
      });
    }
  };

  const handleNextStepClick = async () => {
    try {
      setProcessingLoading(true);

      const response = await updateDueDiligenceProcess(geoDataProcess.id, {
        datasetIdsSelection: geoDataSelection.datasetIdsSelection,
        clusterIdsSelection: geoDataSelection.clusterIdsSelection,
        siteIds: geoDataSelection.siteIds,
      });
      await startGeoDatasProcessing(
        response.data?.updateGeoDataProcess?.geoDataProcess?.geoDatas.map(geoData => geoData.id) ||
          []
      );
      onNextClick();
    } catch (error) {
      console.error(error);
      setErrorMessage('Failed to update the due diligence process');
    } finally {
      setProcessingLoading(false);
    }
  };

  const onGeoDataSiteToggle = (site: ISite) => {
    if (!selectedGeoData) return;

    if (selectedGeoData.dataType === EGeoDataEntity.DATASET) {
      const selectedDataset = geoDataSelection.datasetIdsSelection?.find(
        dataset => dataset.id === selectedGeoData?.datasetId
      );
      const isExcluded = selectedDataset?.excludedSitesIds.includes(site.id);

      if (!selectedDataset) return;

      // Prevent removing the last site from dataset
      if (!isExcluded && farmCount - selectedDataset.excludedSitesIds?.length <= 1) {
        setErrorMessage(
          'You cannot exclude the last site from a dataset. If you want to exude this dataset, please deselect it in the previous step.'
        );
        return;
      }

      // If already excluded, REMOVE from excludedSitesIds (user is selecting)
      // If not excluded, ADD to excludedSitesIds (user is deselecting)
      setGeoDataSelection({
        ...geoDataSelection,
        datasetIdsSelection: geoDataSelection.datasetIdsSelection?.map(dataset => {
          if (dataset.id === selectedGeoData?.datasetId) {
            return {
              ...dataset,
              excludedSitesIds: isExcluded
                ? dataset.excludedSitesIds?.filter(siteId => siteId !== site.id)
                : [...(dataset.excludedSitesIds || []), site.id],
            };
          }
          return dataset;
        }),
      });
    }
    if (selectedGeoData.dataType === EGeoDataEntity.CLUSTER) {
      const selectedCluster = geoDataSelection.clusterIdsSelection?.find(
        cluster => cluster.id === selectedGeoData?.siteClusterId
      );

      const isExcluded = selectedCluster?.excludedSitesIds.includes(site.id);

      if (!selectedCluster) return;

      // Prevent removing the last site from cluster
      if (!isExcluded && farmCount - selectedCluster.excludedSitesIds?.length <= 1) {
        setErrorMessage(
          'You cannot exclude the last site from a cluster. If you want to exude this cluster, please deselect it in the previous step.'
        );
        return;
      }

      setGeoDataSelection({
        ...geoDataSelection,
        clusterIdsSelection: geoDataSelection.clusterIdsSelection?.map(cluster => {
          if (cluster.id === selectedGeoData?.siteClusterId) {
            return {
              ...cluster,
              excludedSitesIds: isExcluded
                ? cluster.excludedSitesIds?.filter(siteId => siteId !== site.id)
                : [...(cluster.excludedSitesIds || []), site.id],
            };
          }
          return cluster;
        }),
      });
    }
  };

  const handleClickGeoData = useCallback(
    (geoDataId: string) => {
      if (selectedGeoDataId !== geoDataId) {
        setGeoDataLoading(true);
      }
      setSelectedGeoDataId(geoDataId);
    },
    [selectedGeoDataId, setSelectedGeoDataId, setGeoDataLoading]
  );

  const handleSearchTermChange = (term: string) => {
    setSearchTerm(term);
    setGeoDataLoading(true);
  };

  useEffect(() => {
    if (!selectedGeoDataId && geoDataProcess.geoDatas.length > 0) {
      const firstSelectableGeoData = geoDataProcess.geoDatas.find(
        ({ dataType }) => EGeoDataEntity.SITE_SELECTION !== dataType
      );
      if (firstSelectableGeoData) {
        handleClickGeoData(firstSelectableGeoData.id);
      } else {
        handleClickGeoData(geoDataProcess.geoDatas[0].id);
      }
    }
  }, [selectedGeoDataId, geoDataProcess.geoDatas, handleClickGeoData]);

  return (
    <>
      <DueDiligenceProcessSubSectionNavigation
        allowNextStepNavigation
        nextStepLoading={processingLoading}
        onPreviousStepClick={onPreviousClick}
        onNextStepClick={handleNextStepClick}
      >
        <Box display="flex" mt={2} gap={2} flexWrap="wrap">
          {geoDataProcess.geoDatas.map(geoData => {
            const excludedSiteIdsFromGeoData = getExcludedSiteIdsForGeoData(geoData);

            return (
              <GeoDataContainer key={geoData.id} onClick={() => handleClickGeoData(geoData.id)}>
                <GeoDataItem
                  title={geoData.title}
                  commodity={geoData.rawMaterial?.title}
                  countryCode={geoData.originCountry}
                  owner={geoData?.ownedBy?.name}
                  active={geoData.id === selectedGeoData?.id}
                  geoDataEntity={EGeoDataEntity[geoData.dataType]}
                  totalCount={geoData.totalOriginalSiteCount}
                  includedCount={
                    excludedSiteIdsFromGeoData && geoData.totalOriginalSiteCount
                      ? geoData.totalOriginalSiteCount - excludedSiteIdsFromGeoData.length
                      : geoData.sites?.count
                  }
                />
              </GeoDataContainer>
            );
          })}
        </Box>
      </DueDiligenceProcessSubSectionNavigation>

      {/* This will only be shown if a only single site is selected inside the process  */}
      {selectedGeoData?.dataType === EGeoDataEntity.SITE_SELECTION && (
        <InfoContainer mt={2}>
          <ThemeTypography variant="BODY_MEDIUM">
            You can not deselect anything here. You can remove this farm by going back and excluding
            it from your selection or press next to continue the process with your selection.
          </ThemeTypography>
        </InfoContainer>
      )}

      {/* Only show something if geo data is selected. It can be the case for single sites that nothing needs to be shown */}
      {selectedGeoData && selectedGeoData.dataType !== EGeoDataEntity.SITE_SELECTION && (
        <>
          <Box mt={2} mb={0.5}>
            <SearchInput
              setDebouncedState={handleSearchTermChange}
              delay={500}
              placeholder="Search"
              disabled={geoDataLoading}
              initialValue={searchTerm}
              inputProps={{
                endAdornment: (
                  <InputAdornment position="start">
                    {searchTerm === '' ? (
                      <Search size={12} />
                    ) : (
                      <CloseButton onClick={() => handleSearchTermChange('')}>
                        <Close size={16} />
                      </CloseButton>
                    )}
                  </InputAdornment>
                ),
              }}
            />
          </Box>

          {geoDataLoading && (
            <Box position="relative" p={5}>
              <Loader />
            </Box>
          )}

          {/* Prevent changes after clicking next and processing already started */}
          {processingLoading && <LoadingOpacity />}

          {error && (
            <Box position="relative" height="100%">
              <ErrorState error={error} />
            </Box>
          )}

          {!farms.length && !geoDataLoading && searchTerm && (
            <Box mt={5}>
              <IllustrationWrapper>
                <Illustration src={TraceableLocation} alt="traceable illustration" />
                <Box mt={3}>
                  <ThemeTypography variant="BODY_LARGER_BOLD" color="GRAY_60">
                    No search results
                  </ThemeTypography>
                </Box>
              </IllustrationWrapper>
            </Box>
          )}

          {farms.length > 0 && !geoDataLoading && (
            <InfiniteScrollWrapper hasMore={hasNextPage} next={handlePageEndReached}>
              <SitesTable
                sites={farms}
                hideHeader
                invertedSelection
                selectedSiteIds={excludedSiteIdsFromSelectedGeoData}
                onSiteSelect={onGeoDataSiteToggle}
                onSiteClick={onGeoDataSiteToggle}
                columns={[
                  SitesTableColumnKeys.SELECTION,
                  SitesTableColumnKeys.TITLE,
                  SitesTableColumnKeys.MODIFIED_TIMESTAMP,
                  SitesTableColumnKeys.ACTIONS,
                ]}
              />
            </InfiniteScrollWrapper>
          )}
        </>
      )}
    </>
  );
};

export default GeoDataCollectionConfirmSelection;
